import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
	dokonciAutorizaci,
	form602SubmissionDeleteQuery,
	useAxiosInstance,
	vypisPolozkaAutorizaceId,
} from '@gov-nx/api';
import { useMessageEvents } from '@gov-nx/core/events';
import { useLocale } from '@gov-nx/core/service';
import { is, isNonEmptyString } from '@gov-nx/core/types';
import type { PageCode } from '@gov-nx/module/page';
import { LayoutGap } from '@gov-nx/ui/web';
import { pageRouteResolver } from '@gov-nx/web';
import { useLatest } from '@gov-nx/core/hooks';
import {
	PARAM_CANCELED_VALUE,
	AUTHORIZATION_ERROR_PARAM,
	AUTHORIZATION_FAILED_STATE_KEY,
	AUTHORIZATION_ID_PARAM,
	AUTHORIZATION_ID_STORAGE_KEY,
	AUTHORIZATION_RESULT_PARAM,
	AUTHORIZATION_RESULT_FAILURE_VALUE,
	AUTHORIZATION_RESULT_SUCCESS_VALUE,
	AUTHORIZATION_CANCELLED_RETURN_URL_KEY,
} from '../../Autorizace/constants';
import { useSessionStorage } from '@gov-nx/utils/web';

export interface AutorizaceViewProps {
	code: PageCode;
}

export const AutorizaceView = ({ code }: AutorizaceViewProps) => {
	const { axiosInstance } = useAxiosInstance();
	const [searchParams] = useSearchParams();
	// NIA returns to /autorizace?Id={authId}&Result=1&Error=... — the branch is
	// decided from these params.
	const isCancelled =
		searchParams.get(AUTHORIZATION_ERROR_PARAM) === PARAM_CANCELED_VALUE;
	const isSuccess =
		is(searchParams.get(AUTHORIZATION_ID_PARAM)) &&
		searchParams.get(AUTHORIZATION_RESULT_PARAM) ===
			AUTHORIZATION_RESULT_SUCCESS_VALUE;
	// Result=0 without the "request canceled" error — authorization failed for
	// another reason (isCancelled is handled first, so this excludes user cancel).
	const isFailure =
		is(searchParams.get(AUTHORIZATION_ID_PARAM)) &&
		searchParams.get(AUTHORIZATION_RESULT_PARAM) ===
			AUTHORIZATION_RESULT_FAILURE_VALUE;
	// Stored on authorization start; used to return the user to where they left
	// off, both on success and on user cancellation.
	const authorizationReturnUrl = useSessionStorage(
		AUTHORIZATION_CANCELLED_RETURN_URL_KEY
	);

	const navigate = useNavigate();
	const latestNavigate = useLatest(navigate);
	const { t } = useLocale(code);

	const hasChecked = useRef(false);

	const { toastMessageSuccess, toastMessageError } = useMessageEvents();

	useEffect(() => {
		if (hasChecked.current) {
			return;
		}
		hasChecked.current = true;

		const authId = searchParams.get(AUTHORIZATION_ID_PARAM);

		// dokoncit closes the authorization on the BE on every outcome and returns
		// its status. Self-contained best-effort: a transport failure is swallowed
		// (returns undefined) and never throws, so it can't suppress the handling
		// that runs after each call. Uses the numeric autorizaceId stored on start —
		// NOT the URL `Id` (ukonId).
		const finalizeAuthorization = async () => {
			const savedAuthorizationId = localStorage.getItem(
				AUTHORIZATION_ID_STORAGE_KEY
			);
			if (
				!is(savedAuthorizationId) ||
				Number.isNaN(Number.parseInt(savedAuthorizationId, 10))
			) {
				return undefined;
			}
			try {
				const { data } = await dokonciAutorizaci({
					axiosInstance,
					authorizationId: Number(savedAuthorizationId),
				});
				return data.status;
			} catch {
				// Transport failure is best-effort — it must not override the
				// Result-based outcome from the NIA return.
				return undefined;
			}
		};

		// Shared failure landing: resolve the temp submission id and redirect to its
		// detail with the failure banner (keeping the submission), else fall back to
		// the dashboard. Used by the Result=0 branch and by a non-OK dokoncit status
		// on the Result=1 branch.
		const redirectToFailedDetail = async () => {
			let detailId: string | undefined;
			try {
				if (is(authId)) {
					const { data } = await vypisPolozkaAutorizaceId({
						axiosInstance,
						ukonId: authId,
					});
					if (isNonEmptyString(data?.polozkaAutorizaceId)) {
						detailId = data.polozkaAutorizaceId;
					}
				}
			} catch {
				// Fall through to the default navigation below.
			}
			localStorage.removeItem(AUTHORIZATION_ID_STORAGE_KEY);
			authorizationReturnUrl.remove();
			toastMessageError(t('page.message.auth-fail'));
			if (isNonEmptyString(detailId)) {
				latestNavigate.current(
					pageRouteResolver('moje-podani.autorizace.detail', {
						formularPodaniId: detailId,
					}),
					{ replace: true, state: { [AUTHORIZATION_FAILED_STATE_KEY]: true } }
				);
				return;
			}
			latestNavigate.current(pageRouteResolver('dashboard'), {
				replace: true,
			});
		};

		if (isCancelled) {
			// Consume the return URL before we wipe local data, so the user lands
			// back where they started.
			const returnUrl =
				authorizationReturnUrl.value ?? pageRouteResolver('dashboard');

			const cancelByUser = async () => {
				// Close the authorization on the BE first — isolated (best-effort), so
				// a rejected finalize cannot skip the cleanup below.
				await finalizeAuthorization();
				try {
					// Resolve the temporary submission id from the authId and remove it
					// — the same cleanup request as the "Zrušit formulář" button.
					if (is(authId)) {
						const { data } = await vypisPolozkaAutorizaceId({
							axiosInstance,
							ukonId: authId,
						});
						if (isNonEmptyString(data?.polozkaAutorizaceId)) {
							await form602SubmissionDeleteQuery({
								axiosInstance,
								formularPodaniId: data.polozkaAutorizaceId,
							});
						}
					}
				} catch {
					// Best-effort cleanup — the user is returned regardless.
				} finally {
					localStorage.removeItem(AUTHORIZATION_ID_STORAGE_KEY);
					authorizationReturnUrl.remove();
					toastMessageError(t('page.message.auth-cancelled'));
					// Stay where the user started — no redirect to the detail.
					latestNavigate.current(returnUrl, { replace: true });
				}
			};

			void cancelByUser();
			return;
		}

		if (isSuccess) {
			// Consume the return URL before local data is wiped, so the user lands
			// back where they started.
			const returnUrl =
				authorizationReturnUrl.value ?? pageRouteResolver('moje-podani');

			const finishSuccess = async () => {
				const status = await finalizeAuthorization();
				// Result=1 is the primary success signal, but stay defensive about the
				// third-party contract: if the BE finalization explicitly refused
				// (non-OK status), treat it as a failure rather than reporting success.
				// A transport error (undefined status) stays best-effort success.
				if (status && status !== 'OK') {
					await redirectToFailedDetail();
					return;
				}
				localStorage.removeItem(AUTHORIZATION_ID_STORAGE_KEY);
				authorizationReturnUrl.remove();
				toastMessageSuccess(t('page.message.auth-success'));
				// Return the user to the place they started, no fixed landing page.
				latestNavigate.current(returnUrl, { replace: true });
			};

			void finishSuccess();
			return;
		}

		if (isFailure) {
			// Authorization failed for a reason other than user cancellation. The
			// temporary submission is KEPT; we redirect the user to its detail in
			// the "Čeká na vyřízení" section and flag the failure so the detail can
			// show the "Autorizace se nezdařila" banner.
			const failByOtherReason = async () => {
				// Close the authorization on the BE first — isolated (best-effort), so
				// a rejected finalize cannot skip the detail resolution/redirect below.
				await finalizeAuthorization();
				await redirectToFailedDetail();
			};

			void failByOtherReason();
			return;
		}

		// No recognized NIA return params — send the user somewhere safe.
		latestNavigate.current(pageRouteResolver('dashboard'), { replace: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps -- runs once on mount; the NIA return params do not change within this view
	}, []);

	return (
		<LayoutGap gap="xl">
			<h5>{t('page.authorization-loading')}</h5>
		</LayoutGap>
	);
};
